import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Clapperboard, ArrowRight, ChevronDown, Loader2 } from "lucide-react";
import { SYMBOL_CENTRAL_PETALS, SYMBOL_OUTER_PETALS } from "@/data";
import { Reveal, Kicker } from "@/components/sections/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RELEASE_DATE = new Date("2028-02-21T08:00:00Z");
const INTERESTS = ["Spiritual Seeker", "Filmmaker", "Investor", "Supporter"];

function useCountdown(target) {
  const [remaining, setRemaining] = useState(() => Math.max(target.getTime() - Date.now(), 0));
  useEffect(() => {
    const id = setInterval(() => setRemaining(Math.max(target.getTime() - Date.now(), 0)), 1000);
    return () => clearInterval(id);
  }, [target]);
  return {
    days: Math.floor(remaining / 86400000),
    hours: Math.floor((remaining % 86400000) / 3600000),
    mins: Math.floor((remaining % 3600000) / 60000),
    secs: Math.floor((remaining % 60000) / 1000),
  };
}

function CountdownUnit({ label, value }) {
  return (
    <div className="w-16 sm:w-20 rounded-2xl bg-white/[0.05] border border-gold/20 py-3 text-center backdrop-blur-sm">
      <AnimatePresence mode="popLayout">
        <motion.div
          key={value}
          initial={{ y: -8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 8, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif text-2xl sm:text-3xl text-gold tabular-nums"
        >
          {String(value).padStart(2, "0")}
        </motion.div>
      </AnimatePresence>
      <div className="text-[10px] uppercase tracking-[0.22em] text-sand/45 mt-1">{label}</div>
    </div>
  );
}

const modalField = "w-full rounded-xl border border-sand/15 bg-white/[0.04] px-4 py-3 text-sand placeholder:text-sand/35 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition";

export default function TheMothersFilm() {
  const { days, hours, mins, secs } = useCountdown(RELEASE_DATE);
  const [showPetals, setShowPetals] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", interest: INTERESTS[0] });
  const [loading, setLoading] = useState(false);

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/film-notify`, form);
      toast.success("You're on the list", { description: "We'll reach out as the 150th anniversary premiere nears." });
      setForm({ name: "", email: "", interest: INTERESTS[0] });
      setModalOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="mothers-film-page" className="bg-ink text-sand pt-32 sm:pt-40 pb-28 relative overflow-hidden">
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[70rem] h-[70rem] rounded-full blur-[150px] opacity-25" style={{ background: "radial-gradient(circle, rgba(230,197,117,0.6), transparent 65%)" }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.4]" style={{ backgroundImage: "radial-gradient(1px 1px at 20% 30%, rgba(255,215,0,0.5) 100%, transparent), radial-gradient(1px 1px at 70% 60%, rgba(255,215,0,0.4) 100%, transparent), radial-gradient(1px 1px at 40% 80%, rgba(255,215,0,0.3) 100%, transparent), radial-gradient(1px 1px at 85% 15%, rgba(255,215,0,0.4) 100%, transparent)", backgroundSize: "600px 600px" }} />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 relative z-10">
        <Link data-testid="back-to-home-link" to="/" className="inline-flex items-center gap-2 text-sm text-sand/55 hover:text-sand transition-colors mb-14">
          <ArrowLeft className="w-4 h-4" /> Back to Auro Ventures
        </Link>

        <Reveal className="text-center">
          <Kicker>150th Birth Anniversary · Sri Aurobindo &amp; The Mother</Kicker>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <Reveal delay={0.05} className="lg:col-span-6 flex flex-col items-center">
            <div className="relative w-72 sm:w-96">
              <div className="absolute -inset-14 rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(230,197,117,0.5), transparent 70%)" }} />
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_conscious-capital/artifacts/obw9lhog_TheMother-removebg-preview.png"
                alt="The Mother"
                data-testid="mothers-portrait-image"
                className="relative w-full h-auto object-contain"
                style={{
                  WebkitMaskImage: "radial-gradient(ellipse 62% 68% at center 42%, black 55%, transparent 92%)",
                  maskImage: "radial-gradient(ellipse 62% 68% at center 42%, black 55%, transparent 92%)",
                  filter: "drop-shadow(0 0 60px rgba(230,197,117,0.35))",
                }}
              />
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-6 text-center lg:text-left">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold/80 border border-gold/25 rounded-full px-3.5 py-1.5">
              <Clapperboard className="w-3.5 h-3.5" /> Animation Film
            </span>
            <h1 className="mt-6 font-serif text-3xl sm:text-5xl leading-[1.08]">Love The Mother</h1>
            <p className="mt-5 text-sand/65 leading-relaxed max-w-lg mx-auto lg:mx-0">
              A hundred and fifty years since the Divine Mother took birth upon this earth, this animated feature follows her aspiration, her vision and her tireless labour to call down the Supramental Light and hasten the evolution of humanity from Ignorance towards the Truth-Consciousness.
            </p>

            <div className="mt-9 flex justify-center lg:justify-start gap-2.5 sm:gap-4" data-testid="film-release-countdown">
              <CountdownUnit label="Days" value={days} />
              <CountdownUnit label="Hours" value={hours} />
              <CountdownUnit label="Min" value={mins} />
              <CountdownUnit label="Sec" value={secs} />
            </div>
            <p className="mt-5 font-serif text-xl text-gold" data-testid="film-release-date">Releasing 21st February 2028</p>

            <button
              data-testid="notify-me-cta-button"
              onClick={() => setModalOpen(true)}
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold text-ink px-7 py-3.5 font-medium hover:bg-saffron transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,215,0,0.4)]"
            >
              Notify Me For Premiere Access
              <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>

        <div className="mt-24 border-t border-sand/10 pt-10 text-center">
          <button
            data-testid="petals-toggle-button"
            onClick={() => setShowPetals((s) => !s)}
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-sand/60 hover:text-gold transition-colors"
          >
            {showPetals ? "Hide" : "Explore"} Sacred Geometry &amp; The 16 Petals
            <motion.span animate={{ rotate: showPetals ? 180 : 0 }} transition={{ duration: 0.3 }}>
              <ChevronDown className="w-4 h-4" />
            </motion.span>
          </button>

          <AnimatePresence>
            {showPetals && (
              <motion.div
                data-testid="petals-expanded-content"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden text-left"
              >
                <div className="mt-14 flex flex-col items-center">
                  <Kicker>Her Sacred Symbol</Kicker>
                  <div className="relative w-56 sm:w-64 mt-6">
                    <div className="absolute -inset-8 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, rgba(230,197,117,0.5), transparent 70%)" }} />
                    <img
                      src="https://customer-assets-eiarnc6j.emergentagent.net/job_conscious-capital/artifacts/c8ukth5o_image.png"
                      alt="The Mother's Symbol"
                      data-testid="mothers-symbol-image"
                      className="relative w-full rounded-full shadow-[0_0_80px_rgba(230,197,117,0.3)]"
                    />
                  </div>
                </div>

                <div className="mt-14">
                  <Kicker>The Four Central Petals</Kicker>
                  <h2 className="mt-4 font-serif text-2xl sm:text-3xl">Her four personalities</h2>
                  <div className="mt-8 rounded-2xl border border-sand/12 overflow-hidden grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-sand/12" data-testid="central-petals-grid">
                    {SYMBOL_CENTRAL_PETALS.map((p) => (
                      <div key={p.name} className="p-6 text-center bg-white/[0.02]">
                        <span className="inline-block w-2 h-2 rounded-full mb-3" style={{ background: p.color, boxShadow: `0 0 8px ${p.color}` }} />
                        <div className="font-serif text-lg">{p.name}</div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-gold">{p.quality}</div>
                        <p className="mt-2.5 text-xs text-sand/50 leading-snug">{p.meaning}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-14 pb-4">
                  <Kicker>The Twelve Outer Petals</Kicker>
                  <h2 className="mt-4 font-serif text-2xl sm:text-3xl">Powers of manifestation</h2>
                  <div className="mt-8 rounded-2xl border border-sand/12 overflow-hidden grid sm:grid-cols-2" data-testid="outer-petals-list">
                    {SYMBOL_OUTER_PETALS.map((p, i) => (
                      <div key={p.name} className={`flex items-center gap-4 px-6 py-4 border-b border-sand/10 ${i % 2 === 0 ? "sm:border-r" : ""} ${i >= SYMBOL_OUTER_PETALS.length - 2 ? "sm:border-b-0" : ""}`}>
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color, boxShadow: `0 0 8px ${p.color}` }} />
                        <div className="flex-1 flex items-baseline justify-between gap-3">
                          <span className="text-sm font-medium text-sand shrink-0">{p.name}</span>
                          <span className="text-xs text-sand/40 text-right">{p.meaning}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="notify-modal" className="bg-[#130D21] border-gold/20 text-sand sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-sand">Reserve Your Seat</DialogTitle>
            <DialogDescription className="text-sand/55">For the 150th Anniversary premiere of the feature film.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNotify} className="space-y-4 mt-2">
            <input data-testid="notify-modal-name-input" className={modalField} placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="notify-modal-email-input" type="email" className={modalField} placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <select data-testid="notify-modal-interest-select" className={modalField} value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}>
              {INTERESTS.map((i) => <option key={i} className="bg-[#130D21]">{i}</option>)}
            </select>
            <button data-testid="notify-modal-submit-button" disabled={loading} type="submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-3.5 font-medium hover:bg-saffron transition-colors duration-300 disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Reserve My Seat"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
